import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import type { UnpackResponse } from "../../../api/src/lib";
import type { AdminController } from "../../../api/src/admin/admin.controller";

// The cross-firm Explain bundle returned by the staff-guarded
// `GET /admin/rebalances/:rebalanceId/explain` endpoint
// (s-read-only-admin-explain-019ea1c7 R8). Carries the owning firm, the
// household + accounts (for the v3 account resolver), the v3 explain payload
// (`null` ⇒ no-data state), and the checks payload — so the v3 tree renders
// without issuing any further firm-scoped request.
export type AdminRebalanceExplainBundle = UnpackResponse<
  AdminController["getRebalanceExplain"]
>["data"];

// The endpoint wraps the bundle in `{ data }` (see api `wrapResponse`).
type WrappedBundle = UnpackResponse<AdminController["getRebalanceExplain"]>;

// Terminal load states for the read-only admin Explain viewer. Exactly one is
// reached after `loading` resolves (R9–R12 / AC11):
//   - `not-found`: 404 — unknown/invalid rebalance id (R10 / AC8)
//   - `error`:     any non-404 failure (5xx, network, timeout) (R11 / AC9)
//   - `ok`:        bundle loaded; `explain === null` is the no-data state
//                  (R12 / AC10), distinct from `not-found`.
export type AdminExplainStatus = "loading" | "not-found" | "error" | "ok";

export class HttpStatusError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "HttpStatusError";
  }
}

export interface UseAdminRebalanceExplainResult {
  status: AdminExplainStatus;
  bundle: AdminRebalanceExplainBundle | undefined;
  /** Send the bundle read again (the error state's "Try again", R32). */
  retry: () => void;
}

/**
 * Fetch the cross-firm Explain bundle for the read-only admin viewer.
 *
 * Reads the raw HTTP status so a 404 (rebalance not found, R10) is
 * distinguished from any other failure (R11) — the deprecated
 * `useAuthenticatedFetch` swallows the status into an opaque Error, so this
 * GET-only path is fetched directly. GET-only: this view issues no mutating
 * request (R3 / AC3).
 */
export function useAdminRebalanceExplain(
  rebalanceId: number | undefined,
): UseAdminRebalanceExplainResult {
  const { getAccessTokenSilently } = useAuth0();
  const enabled = rebalanceId != null && Number.isInteger(rebalanceId);

  const query = useQuery<AdminRebalanceExplainBundle, HttpStatusError>({
    queryKey: ["admin", "rebalances", rebalanceId, "explain"],
    enabled,
    retry: false,
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const res = await fetch(
        `${process.env.REACT_APP_API_ROOT}/admin/rebalances/${rebalanceId}/explain`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (!res.ok) {
        throw new HttpStatusError(res.status, `Request failed (${res.status})`);
      }
      const body = (await res.json()) as WrappedBundle;
      return body.data;
    },
  });

  // Only the bundle read is sent again; the audit read is its own query (R30).
  const { refetch } = query;
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (!enabled || query.isPending) {
    return { status: "loading", bundle: undefined, retry };
  }
  if (query.isError) {
    return {
      status: query.error.status === 404 ? "not-found" : "error",
      bundle: undefined,
      retry,
    };
  }
  return { status: "ok", bundle: query.data, retry };
}
